import axios, { AxiosInstance } from 'axios';
import { CognitoUserPool } from 'amazon-cognito-identity-js';

// Cognito configuration (same as in cognitoAuth.ts)
const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID!,
  ClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,
};

const userPool = new CognitoUserPool(poolData);

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      timeout: 100000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      async (config) => {
        try {
          // Get current Cognito session token
          const cognitoUser = userPool.getCurrentUser();
          if (cognitoUser) {
            const session = await new Promise((resolve, reject) => {
              cognitoUser.getSession((err: Error | null, session: any) => {
                if (err) reject(err);
                else resolve(session);
              });
            });

            if (session && (session as any).isValid()) {
              const token = (session as any).getIdToken().getJwtToken();
              config.headers.Authorization = `Bearer ${token}`;
            }
          }
        } catch (error) {
          console.warn('Failed to get Cognito token:', error);
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - logout and redirect to login
          const cognitoUser = userPool.getCurrentUser();
          if (cognitoUser) {
            cognitoUser.signOut();
          }
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
        } else if (error.response?.status === 403) {
          // Handle forbidden - subscription required
          const data = error.response?.data;
          if (data?.redirectTo) {
            window.location.href = data.redirectTo;
          } else {
            // Default redirect to subscription page
            window.location.href = '/subscription';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async getUserProfile() {
    try {
      const response = await this.api.get('/user/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw new Error('Failed to fetch user profile');
    }
  }

  async updateUserProfile(data: { name: string }) {
    try {
      const response = await this.api.put('/user/profile', data);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error('Failed to update user profile');
    }
  }

  async getAudioTracks() {
    try {
      const response = await this.api.get('/audio/tracks');
      return response.data;
    } catch (error) {
      console.error('Error fetching audio tracks:', error);
      throw new Error('Failed to fetch audio tracks');
    }
  }

  async getAudioTrack(trackId: string, duration: number) {
    try {
      const response = await this.api.get(
        `/audio/track/${trackId}?duration=${duration}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching audio track:', error);
      throw new Error('Failed to fetch audio track');
    }
  }

  async getSubscription() {
    try {
      const response = await this.api.get('/subscription');
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw new Error('Failed to fetch subscription');
    }
  }

  async createSubscription(planType: 'monthly' | 'annual') {
    try {
      const response = await this.api.post('/subscription/create', {
        planType,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      // Return detailed error message from API if available
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to create subscription';
      throw new Error(errorMessage);
    }
  }

  async cancelSubscription() {
    try {
      const response = await this.api.post('/subscription/cancel');
      return response.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  }

  async resumeSubscription() {
    try {
      const response = await this.api.post('/subscription/resume');
      return response.data;
    } catch (error) {
      console.error('Error resuming subscription:', error);
      throw new Error('Failed to resume subscription');
    }
  }

  async updatePaymentMethod() {
    try {
      const response = await this.api.post(
        '/subscription/update-payment-method'
      );
      return response.data;
    } catch (error) {
      console.error('Error getting payment update transaction:', error);
      throw error;
    }
  }

  async startTrial() {
    try {
      const response = await this.api.post('/subscription/start-trial');
      return response.data;
    } catch (error) {
      console.error('Error starting trial:', error);
      throw new Error('Failed to start trial');
    }
  }
}

export const apiService = new ApiService();
